import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Settings, Printer, CalendarDays, Save, Info, RefreshCcw,
  CheckCircle, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * PresupuestosConfig
 * - Página de configuración para Presupuestos (POS)
 * - Se guarda en organizations.print_config (jsonb)
 *
 * Claves:
 * - quote_print_mode: 'ask' | 'always' | 'never'
 * - quote_due_days: number (0 = sin vencimiento)
 * - quote_printer_name: string (preferencia interna / label)
 *
 * Reglas:
 * - Sin modales del navegador
 * - Diseño estilo "Productos": header + acción arriba, cards sobrias, sin azules oscuros.
 * - Botón confirmación verde con check (estándar Impulza)
 */

const GREEN = '#3FF2AA';
const GREEN_TEXT = '#0B2A36';

const safeObj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const clampInt = (v, min = 0, max = 3650) => {
  const n = Number.parseInt(String(v ?? ''), 10);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
};

const PresupuestosConfig = ({ onNavigate }) => {
  const { user, profile, organization, refreshOrganization } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [orgId, setOrgId] = useState(null);
  const [orgRow, setOrgRow] = useState(null);

  // Config (draft)
  const [printMode, setPrintMode] = useState('ask'); // ask | always | never
  const [dueDays, setDueDays] = useState(7); // 0 = sin vencimiento
  const [printerName, setPrinterName] = useState('');

  // UI confirm embebido (botón verde con check)
  const [savedPulse, setSavedPulse] = useState(false);

  const go = (module, entityId = null) => {
    try {
      if (typeof onNavigate === 'function') return onNavigate(module, entityId);
      if (entityId) return window.location.assign(`/sistema/${module}/${entityId}`);
      return window.location.assign(`/sistema/${module}`);
    } catch (e) {
      console.error('Error navegando:', e);
      toast.error('No se pudo navegar. Revisá rutas en Sistema.jsx');
    }
  };

  const modeLabel = useMemo(() => {
    if (printMode === 'always') return 'SIEMPRE';
    if (printMode === 'never') return 'NUNCA';
    return 'PREGUNTAR';
  }, [printMode]);

  const modeDesc = useMemo(() => {
    if (printMode === 'always') return 'Al generar presupuesto, se abre la vista y dispara impresión automáticamente.';
    if (printMode === 'never') return 'No se muestra vista previa ni se pregunta por impresión.';
    return 'Al generar presupuesto, se muestra una vista previa y el usuario decide si imprime.';
  }, [printMode]);

  const dueDesc = useMemo(() => {
    if (Number(dueDays) <= 0) return 'Sin vencimiento: el presupuesto no mostrará fecha límite.';
    return `El presupuesto será válido por ${dueDays} día(s) desde la fecha de emisión.`;
  }, [dueDays]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, organization?.id]);

  const load = async () => {
    setLoading(true);
    try {
      // profile + organization vienen del AuthContext (un fetch por sesión).
      if (!profile?.organization_id) throw new Error('Sin organization_id');

      setOrgId(profile.organization_id);
      setOrgRow(organization || null);

      const pc = safeObj(organization?.print_config);

      const pm = String(pc?.quote_print_mode || 'ask');
      const dd = Number(pc?.quote_due_days ?? 7);
      const pn = String(pc?.quote_printer_name || '');

      setPrintMode(['ask', 'always', 'never'].includes(pm) ? pm : 'ask');
      setDueDays(Number.isFinite(dd) ? dd : 7);
      setPrinterName(pn);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const persist = async (patch) => {
    if (!orgId) return;
    setSaving(true);
    try {
      // Traer la print_config actual y mergear sin pisar otras claves
      const current = safeObj(orgRow?.print_config);
      const next = { ...current, ...patch };

      const { error } = await supabase
        .from('organizations')
        .update({ print_config: next })
        .eq('id', orgId);

      if (error) throw error;

      // Estado local + refrescamos el AuthContext para que otras páginas vean los cambios.
      setOrgRow((p) => (p ? ({ ...p, print_config: next }) : p));
      await refreshOrganization?.();
      setSavedPulse(true);
      setTimeout(() => setSavedPulse(false), 1100);
      toast.success('Configuración guardada');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    const dd = clampInt(dueDays, 0, 3650);
    const pm = ['ask', 'always', 'never'].includes(printMode) ? printMode : 'ask';
    const pn = String(printerName || '').trim();

    // Actualiza estados (por si venían valores raros)
    setDueDays(dd);
    setPrintMode(pm);
    setPrinterName(pn);

    await persist({
      quote_print_mode: pm,
      quote_due_days: dd,
      quote_printer_name: pn
    });
  };

  if (loading) {
    return (
      <div className="h-full min-h-screen flex flex-col items-center justify-center bg-[#F2F9FF] p-10">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={36} />
        <p className="font-black uppercase tracking-[0.3em] text-slate-400 text-[10px]">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 md:p-6 animate-nova-in bg-[#F2F9FF]">
      {/* HEADER (modelo Productos) */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md">
            <Settings size={18} className="text-[#3FF2AA]" />
          </div>

          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">
              Configuración de Presupuestos
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Impresión · vencimiento · preferencia operativa
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => go('ventas')}
            className="px-4 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-slate-800 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Volver"
          >
            <span className="text-slate-400">←</span> Volver
          </button>

          <button
            onClick={load}
            className="px-4 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm text-slate-600 hover:text-slate-900 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Actualizar"
          >
            <RefreshCcw size={14} /> Actualizar
          </button>

          {/* Botón verde estándar con check */}
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60 active:scale-[0.99]"
            title="Guardar"
          >
            {saving ? <RefreshCcw size={16} className="animate-spin" /> : savedPulse ? <CheckCircle size={16} /> : <Save size={16} />}
            {saving ? 'Guardando...' : (savedPulse ? 'Guardado' : 'Guardar')}
          </button>
        </div>
      </div>

      {/* KPI / CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Modo impresión</p>
            <Printer size={16} className="text-slate-300" />
          </div>
          <p className="mt-2 text-xl font-black text-slate-800 tracking-tight">{modeLabel}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Comportamiento en POS</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Vencimiento</p>
            <CalendarDays size={16} className="text-slate-300" />
          </div>
          <p className="mt-2 text-xl font-black text-slate-800 tracking-tight">
            {Number(dueDays) > 0 ? `${dueDays} día(s)` : 'Sin VTO'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Validez comercial</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Impresora</p>
            <Printer size={16} className="text-slate-300" />
          </div>
          <p className="mt-2 text-sm font-black text-slate-800 tracking-tight truncate" title={printerName || ''}>
            {printerName ? printerName : 'No definida'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Preferencia interna</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Empresa</p>
            <Settings size={16} className="text-slate-300" />
          </div>
          <p className="mt-2 text-sm font-black text-slate-800 tracking-tight truncate" title={orgRow?.name || ''}>
            {orgRow?.name || '—'}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Contexto</p>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Izquierda: Form */}
        <div className="lg:col-span-2 space-y-3">
          {/* Modo de impresión */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
              <Printer size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Impresión de Presupuestos
              </span>
            </div>

            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-6">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Modo de impresión
                  </label>

                  <select
                    value={printMode}
                    onChange={(e) => setPrintMode(e.target.value)}
                    className="mt-1 w-full bg-white border border-slate-200 px-4 py-3 rounded-2xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-sm"
                  >
                    <option value="ask">Preguntar (recomendado)</option>
                    <option value="always">Siempre imprimir</option>
                    <option value="never">No imprimir</option>
                  </select>

                  <p className="mt-2 text-[10px] font-bold text-slate-500 leading-relaxed">
                    {modeDesc}
                  </p>
                </div>

                <div className="md:col-span-6">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Impresora preferida (opcional)
                  </label>

                  <input
                    value={printerName}
                    onChange={(e) => setPrinterName(e.target.value)}
                    className="mt-1 w-full bg-white border border-slate-200 px-4 py-3 rounded-2xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-sm"
                    placeholder="Ej: Epson TM-T20 / Oficina / Caja 1"
                  />

                  <p className="mt-2 text-[10px] font-bold text-slate-500 leading-relaxed">
                    Nota: es una preferencia interna. El navegador puede requerir confirmación al imprimir.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-2xl bg-slate-50 border border-slate-100">
                <Info size={14} className="text-slate-300 mt-0.5" />
                <p className="text-[10px] font-bold text-slate-500 leading-relaxed">
                  Esta configuración aplica a <span className="font-black">Punto de Venta</span> cuando el usuario elige
                  <span className="font-black"> Presupuesto</span>. Para ventas reales, el flujo sigue siendo “Factura”.
                </p>
              </div>
            </div>
          </div>

          {/* Vencimiento */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
              <CalendarDays size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Validez y vencimiento
              </span>
            </div>

            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-6">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    Días de validez (0 = sin vencimiento)
                  </label>
                  <input
                    type="number"
                    value={dueDays}
                    onChange={(e) => setDueDays(e.target.value)}
                    className="mt-1 w-full bg-white border border-slate-200 px-4 py-3 rounded-2xl text-[11px] font-black outline-none focus:border-[#51A7FD] shadow-sm"
                    min={0}
                    max={3650}
                  />
                  <p className="mt-2 text-[10px] font-bold text-slate-500 leading-relaxed">{dueDesc}</p>
                </div>

                <div className="md:col-span-6">
                  <div className="mt-2 p-4 rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                      Comportamiento en el documento
                    </p>
                    <p className="mt-2 text-[11px] font-bold text-slate-700 leading-relaxed">
                      Si definís vencimiento, el presupuesto muestra:
                      <span className="font-black"> “Válido hasta: dd/mm/aaaa”</span>.
                      Esto evita presupuestos “eternos” y mejora control comercial.
                    </p>
                  </div>
                </div>
              </div>

              {Number(dueDays) > 365 ? (
                <div className="flex items-start gap-2 p-3 rounded-2xl bg-amber-50 border border-amber-100">
                  <AlertCircle size={14} className="text-amber-500 mt-0.5" />
                  <p className="text-[10px] font-bold text-amber-700 leading-relaxed">
                    Un vencimiento mayor a 365 días suele ser riesgoso (precios desactualizados). No lo prohibo, pero te aviso.
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Derecha: Resumen */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
              <Settings size={16} className="text-[#3FF2AA]" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">
                Resumen operativo
              </span>
            </div>

            <div className="p-4 space-y-3">
              <div className="p-3 rounded-2xl border border-slate-200 bg-white shadow-sm">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Impresión</p>
                <p className="mt-1 text-[11px] font-black text-slate-800">{modeLabel}</p>
                <p className="text-[10px] font-bold text-slate-500 mt-1">{modeDesc}</p>
              </div>

              <div className="p-3 rounded-2xl border border-slate-200 bg-white shadow-sm">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Vencimiento</p>
                <p className="mt-1 text-[11px] font-black text-slate-800">
                  {Number(dueDays) > 0 ? `${dueDays} día(s)` : 'Sin vencimiento'}
                </p>
                <p className="text-[10px] font-bold text-slate-500 mt-1">{dueDesc}</p>
              </div>

              <div className="p-3 rounded-2xl border border-slate-200 bg-white shadow-sm">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Impresora</p>
                <p className="mt-1 text-[11px] font-black text-slate-800">
                  {printerName ? printerName : 'No definida'}
                </p>
                <p className="text-[10px] font-bold text-slate-500 mt-1">
                  Se usa como referencia en la UI (no fuerza impresora del navegador).
                </p>
              </div>

              <button
                type="button"
                onClick={handleSaveAll}
                disabled={saving}
                className="w-full px-5 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99]"
                style={{ background: GREEN, color: GREEN_TEXT }}
              >
                {saving ? <RefreshCcw size={16} className="animate-spin" /> : savedPulse ? <CheckCircle size={16} /> : <Save size={16} />}
                {saving ? 'Guardando...' : (savedPulse ? 'Guardado' : 'Guardar configuración')}
              </button>

              <div className="text-[10px] font-bold text-slate-400 text-center">
                Cambios aplican a futuros presupuestos.
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default PresupuestosConfig;
